import { ClientForm } from "@/components/clients/client-form";
import { Button } from "@/components/core/buttons/button";
import { useClientUpdateMutation } from "@/features/dashboard/api/queries/client-query";
import type { Client } from "@/features/dashboard/types/client";
import * as Dialog from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";

interface EditClientModalProps {
	client: Client;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export function EditClientModal({ client, isOpen, onClose, onSuccess }: EditClientModalProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const updateMutation = useClientUpdateMutation(client.id?.toString());

	const handleSubmit = async (values: Client) => {
		setIsSubmitting(true);
		try {
			await updateMutation.mutateAsync(values);
			toast.success("Client updated successfully");
			onSuccess?.();
			onClose();
		} catch (error) {
			console.log(error);
			toast.error("Failed to update client");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Edit Client</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<ClientForm client={client} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="client-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#client-form") as HTMLFormElement;
								if (form) {
									form.requestSubmit();
								}
							}}
						>
							{isSubmitting ? "Updating..." : "Update Client"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
